import { useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Somebody's face, or their initials.
 *
 * ⚠️ The src points at THIS origin — `/api/users/{id}/avatar` — which authorises the
 * caller, checks the picture has passed a malware scan, and then 302s to a short-lived
 * URL on the blob host. The bytes never come from the app origin, so an image decoder bug
 * is exercised against a hostname holding no session.
 *
 * Every failure resolves to initials: no picture, not yet scanned, scan failed, storage
 * unconfigured, network down. The endpoint answers 404 for all of them without saying
 * which, and `onError` below turns that into the same fallback a person without a picture
 * gets. There is no broken-image state and nothing to explain.
 */
export function Avatar({
  userId,
  name,
  hasPicture = true,
  size = "md",
  className,
}: {
  userId: string;
  name: string;

  /**
   * What the server said about whether a picture exists AND has passed a scan.
   *
   * ⚠️ Defaults to true, so a caller that has no such answer behaves exactly as this
   * component always did: request it, and fall back to initials on the 404.
   *
   * Passing it matters on lists. Most people have never uploaded a picture, so a queue of
   * 25 rows without this hint is 25 requests that 404 — a wasted round trip each and a
   * console full of errors that hides the real ones. It is an optimisation, never a gate:
   * the endpoint still authorises the caller and still refuses an unscanned picture, so a
   * client that lies to itself here gets a 404 and initials.
   */
  hasPicture?: boolean;

  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  const sizes = {
    sm: "h-6 w-6 text-[10px]",
    md: "h-8 w-8 text-xs",
    lg: "h-12 w-12 text-sm",
    xl: "h-24 w-24 text-xl",
  } as const;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-muted font-medium text-muted-foreground",
        sizes[size],
        className,
      )}
      // The name, not the image: a screen reader wants who this is, and the picture is
      // decoration on top of that.
      title={name}
    >
      {failed || !hasPicture ? (
        <span aria-hidden>{initialsOf(name)}</span>
      ) : (
        <img
          src={`/api/users/${encodeURIComponent(userId)}/avatar`}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
      <span className="sr-only">{name}</span>
    </span>
  );
}

/**
 * Up to two initials from a display name.
 *
 * Takes the first and last word so "Maria del Carmen Ortiz" reads MO rather than MD.
 * Uses the code-point iterator rather than charAt, because a name can legitimately start
 * with a character outside the basic plane and slicing one in half renders as a box.
 */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return "?";

  const first = [...words[0]][0] ?? "";
  const last = words.length > 1 ? ([...words[words.length - 1]][0] ?? "") : "";

  return (first + last).toUpperCase();
}
